// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AppAccessCandidateSchema, AppAccessRoleSchema, AppGroupBindingSchema, AppGroupMembersSchema, PaginationQuerySchema, } from '@greenlight/shared';
import { AppIdParamsSchema, errorResponses } from './common.js';
const c = initContract();
// The param carries the binding row id (`app_rbac_groups.id`), not a group id —
// `group_id` always means `groups.id` on this surface.
const BindingParamsSchema = AppIdParamsSchema.extend({ bindingId: z.string().uuid() });
// The body names exactly one source plus the role it confers. Two strict
// members rather than one object with optional keys, so naming both sources (or
// neither) fails validation instead of being silently resolved.
export const AddAppGroupBindingSchema = z.union([
    z.object({ group_id: z.string().uuid(), role: AppAccessRoleSchema }).strict(),
    z.object({ idp_group_registration_id: z.string().uuid(), role: AppAccessRoleSchema }).strict(),
]);
// A role edit names only the role: the source is fixed at bind time, and
// re-pointing a binding at a different group is an unbind and a bind.
export const UpdateAppGroupBindingSchema = z.object({ role: AppAccessRoleSchema }).strict();
// The membership read names the group, not the binding, because the rule it
// enforces is about the group: an owner may read one their app is bound to.
export const AppGroupMembersQuerySchema = PaginationQuerySchema.extend({
    group_id: z.string().uuid().optional(),
    idp_group_registration_id: z.string().uuid().optional(),
}).refine((q) => (q.group_id === undefined) !== (q.idp_group_registration_id === undefined), 'Name exactly one of group_id or idp_group_registration_id.');
// Two characters is the shortest query worth a round trip, and there is no
// unfiltered mode: the Access tab resolves a name an owner is already typing,
// and never hands a `user`-role owner the org's roster (docs/41 §RBAC and audit).
export const AppAccessCandidatesQuerySchema = z.object({
    q: z.string().trim().min(2).max(255),
    limit: z.coerce.number().int().min(1).max(25).optional(),
});
export const rbacContract = c.router({
    list: {
        method: 'GET',
        path: '/api/apps/:id/rbac-groups',
        pathParams: AppIdParamsSchema,
        responses: {
            200: z.object({ items: z.array(AppGroupBindingSchema) }),
            ...errorResponses,
        },
        summary: 'List the groups bound to this app, both sources.',
    },
    add: {
        method: 'POST',
        path: '/api/apps/:id/rbac-groups',
        pathParams: AppIdParamsSchema,
        body: AddAppGroupBindingSchema,
        responses: { 201: AppGroupBindingSchema, ...errorResponses },
        summary: 'Bind a Greenlight group or a registered IdP group to this app.',
    },
    updateRole: {
        method: 'PATCH',
        path: '/api/apps/:id/rbac-groups/:bindingId',
        pathParams: BindingParamsSchema,
        body: UpdateAppGroupBindingSchema,
        responses: { 200: AppGroupBindingSchema, ...errorResponses },
        summary: "Change a group binding's role between viewer and editor.",
    },
    remove: {
        method: 'DELETE',
        path: '/api/apps/:id/rbac-groups/:bindingId',
        pathParams: BindingParamsSchema,
        body: c.noBody(),
        responses: { 204: c.noBody(), ...errorResponses },
        summary: 'Remove a group binding from this app.',
    },
    listGroupMembers: {
        method: 'GET',
        path: '/api/apps/:id/group-members',
        pathParams: AppIdParamsSchema,
        query: AppGroupMembersQuerySchema,
        responses: { 200: AppGroupMembersSchema, ...errorResponses },
        summary: 'List who a group bound to this app admits; 403 if it is not bound to it.',
    },
    searchCandidates: {
        method: 'GET',
        path: '/api/apps/:id/access-candidates',
        pathParams: AppIdParamsSchema,
        query: AppAccessCandidatesQuerySchema,
        responses: { 200: z.object({ items: z.array(AppAccessCandidateSchema) }), ...errorResponses },
        summary: 'Resolve a search term into people, Greenlight groups, and registered IdP groups.',
    },
});
//# sourceMappingURL=rbac.js.map